// ─── src/components/sections/VendorPreview.jsx ──────────────────────────────
import { useState, useEffect } from "react";
import { useTheme } from "../../theme/ThemeContext";
import { useShortlist } from "../../shortlist/ShortlistContext";
import { GLOBAL_VENDORS } from "../../data/globalVendors";
import { track } from "../../utils/track";
import { transformListing, assertCanonical } from "../../utils/transformListing";
import LuxuryVendorCard from "../cards/LuxuryVendorCard";
import SliderNav from "../ui/SliderNav";
import QuickViewModal from "../modals/QuickViewModal";

const GD = "var(--font-heading-primary)";
const NU = "var(--font-body)";

function useIsMobile(bp = 768) {
  const [mobile, setMobile] = useState(() => window.innerWidth <= bp);
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${bp}px)`);
    const fn = (e) => setMobile(e.matches);
    mql.addEventListener("change", fn);
    return () => mql.removeEventListener("change", fn);
  }, [bp]);
  return mobile;
}

export default function VendorPreview({ onViewVendor, dbVendors, onViewCategory }) {
  const C = useTheme();
  const { isShortlisted, toggleItem } = useShortlist();
  const [quickViewItem, setQuickViewItem] = useState(null);
  const isMobile = useIsMobile();

  // Use live DB vendors when available (already canonical from H7/H3 pipeline);
  // fall back to static data routed through transformListing.
  // GLOBAL_VENDORS uses `price` and `includes` as field names; normalise
  // those to the canonical shape before transforming.
  const featured = (dbVendors && dbVendors.length > 0)
    ? dbVendors.slice(0, 6)
    : GLOBAL_VENDORS.filter((v) => v.featured).slice(0, 6).map((v) =>
        transformListing({
          ...v,
          priceFrom:   v.price      ?? v.priceFrom,
          specialties: v.includes   ?? v.specialties,
          type:        "vendor",
        })
      );

  // No real vendors yet → a premium "by invitation" message, never fake cards.
  if (featured.length === 0) {
    return (
      <section id="vendor-preview" aria-label="Selected wedding creatives"
        style={{ background: "#0b0906", padding: "clamp(72px, 12vh, 130px) 24px", textAlign: "center" }}>
        <div style={{ maxWidth: 620, margin: "0 auto" }}>
          <div style={{ fontFamily: "var(--font-body)", fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase", color: "#d39a21", marginBottom: 18 }}>The Creatives</div>
          <h2 style={{ fontFamily: "var(--font-heading-primary)", fontWeight: 400, fontSize: "clamp(28px, 4vw, 44px)", color: "#f5f0e8", lineHeight: 1.1, margin: "0 0 22px" }}>Selected Wedding Creatives</h2>
          <p style={{ fontFamily: "var(--font-body)", fontSize: "clamp(14px, 1.6vw, 16px)", lineHeight: 1.7, color: "rgba(245,240,232,0.62)", margin: "0 auto 12px", maxWidth: 540 }}>
            We are currently welcoming a carefully chosen collection of luxury planners, photographers, florists and designers.
          </p>
          <p style={{ fontFamily: "var(--font-body)", fontSize: 13, letterSpacing: "0.04em", color: "rgba(211,154,33,0.8)", margin: 0 }}>
            Every creative is individually reviewed before publication.
          </p>
        </div>
      </section>
    );
  }

  // Mobile: vertical feed. Desktop: horizontal carousel
  if (isMobile) {
    return (
      <>
        <div
          id="vendor-preview"
          aria-label="Handpicked vendors – Mobile feed"
          className="home-vendor-preview-mobile"
          style={{
            position: "relative",
            background: C.black,
            width: "100vw",
            height: "100dvh",
            overflowX: "hidden",
            overflowY: "auto",
            scrollSnapType: "y mandatory",
            display: "flex",
            flexDirection: "column",
            margin: 0,
            padding: 0,
            gap: 0,
          }}
        >
          {featured.map((v) => {
            assertCanonical(v, "VendorPreview/mobile");
            return (
              <div
                key={v.id}
                className="home-vendor-card"
                style={{
                  width: "100vw",
                  flex: "0 0 100dvh",
                  scrollSnapAlign: "start",
                  scrollMarginTop: 0,
                  borderRadius: 0,
                  margin: 0,
                  padding: 0,
                }}
              >
                <LuxuryVendorCard
                  v={v}
                  isMobile={true}
                  onView={() => {
                    track("card_click", { id: v.id });
                    onViewVendor?.(v);
                  }}
                  onQuickView={() => {
                    track("card_quick_view", { id: v.id });
                  }}
                  quickViewItem={quickViewItem}
                  setQuickViewItem={setQuickViewItem}
                  imgWidth={720}
                />
              </div>
            );
          })}
        </div>
        {/* Quick View modal - outside overflow clip */}
        {quickViewItem && (
          <QuickViewModal
            item={quickViewItem}
            onClose={() => setQuickViewItem(null)}
            onViewFull={() => {
              const raw = featured.find((f) => f.id === quickViewItem.id);
              setQuickViewItem(null);
              onViewVendor?.(raw ?? quickViewItem);
            }}
          />
        )}
      </>
    );
  }

  // Desktop: Section with heading + carousel + CTA
  return (
    <>
      <section
        id="vendor-preview"
        aria-label="Handpicked vendors"
        className="home-vendor-preview"
        style={{
          position: "relative",
          background: C.black,
          padding: "110px 60px",
          overflow: "hidden",
        }}
      >
        {/* Watermark */}
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 110,
            left: "50%",
            transform: "translateX(-50%)",
            fontFamily: "var(--font-heading-primary)",
            fontSize: "clamp(120px, 12vw, 180px)",
            fontWeight: 400,
            fontStyle: "italic",
            color: "rgba(211,154,33,0.06)",
            whiteSpace: "nowrap",
            pointerEvents: "none",
            userSelect: "none",
            letterSpacing: "-2px",
            lineHeight: 1,
            zIndex: 0,
          }}
        >
          The Creatives
        </span>
        <div style={{ maxWidth: 1320, margin: "0 auto", position: "relative", zIndex: 1 }}>
          {/* Heading */}
          <div style={{ marginBottom: 56 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                marginBottom: 16,
              }}
            >
              <div
                style={{ width: 28, height: 1, background: "rgba(211,154,33,0.5)" }}
              />
              <span
                style={{
                  fontFamily: NU,
                  fontSize: 10,
                  letterSpacing: "0.28em",
                  textTransform: "uppercase",
                  color: C.gold,
                  fontWeight: 600,
                }}
              >
                Handpicked for You
              </span>
            </div>
            <h2
              style={{
                fontFamily: GD,
                fontSize: "clamp(32px, 3.5vw, 52px)",
                color: C.off,
                fontWeight: 400,
                lineHeight: 1.1,
              }}
            >
              The Finest{" "}
              <span style={{ fontStyle: "italic", color: C.gold }}>
                The Creatives
              </span>
            </h2>
            <p
              style={{
                fontFamily: NU,
                fontSize: 14,
                color: C.grey,
                lineHeight: 1.7,
                maxWidth: 560,
                marginTop: 14,
                fontWeight: 300,
              }}
            >
              Curated venues and creatives, refined by style, guest count,
              and region. Trusted by couples planning weddings across Europe
              and beyond.
            </p>
          </div>

          {/* Card slider */}
          <div style={{ marginBottom: 48 }}>
            <SliderNav className="home-vendor-grid" cardWidth={360} gap={24}>
              {featured.map((v) => {
                assertCanonical(v, "VendorPreview/desktop");
                return (
                  <div key={v.id} className="home-vendor-card" style={{ flex: "0 0 360px", scrollSnapAlign: "start" }}>
                    <LuxuryVendorCard
                      v={v}
                      isMobile={false}
                      onView={() => {
                        track("card_click", { id: v.id });
                        onViewVendor?.(v);
                      }}
                      onQuickView={() => {
                        track("card_quick_view", { id: v.id });
                      }}
                      quickViewItem={quickViewItem}
                      setQuickViewItem={setQuickViewItem}
                      imgWidth={720}
                    />
                  </div>
                );
              })}
            </SliderNav>
          </div>

          {/* CTA */}
          <div style={{ textAlign: "center" }}>
            <button
              onClick={() => onViewCategory?.({ category: "wedding-vendors" })}
              style={{
                background: "transparent",
                color: C.gold,
                border: `1px solid rgba(211,154,33,0.4)`,
                borderRadius: "var(--lwd-radius-card)",
                padding: "14px 40px",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                cursor: "pointer",
                fontFamily: NU,
                transition: "all 0.25s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = C.gold;
                e.currentTarget.style.color = "#0a0906";
                e.currentTarget.style.borderColor = C.gold;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = C.gold;
                e.currentTarget.style.borderColor = "rgba(211,154,33,0.4)";
              }}
            >
              View All Vendors →
            </button>
          </div>
        </div>
      </section>

      {/* Quick View modal - outside overflow clip */}
      {quickViewItem && (
        <QuickViewModal
          item={quickViewItem}
          onClose={() => setQuickViewItem(null)}
          onViewFull={() => {
            const raw = featured.find((f) => f.id === quickViewItem.id);
            setQuickViewItem(null);
            onViewVendor?.(raw ?? quickViewItem);
          }}
        />
      )}
    </>
  );
}
